// Helper functions for sorting attendance data
import { DAY_ORDER } from "../../common/Const";
import { isGroupReportLessonType } from "../../common/lessonTypes";

export const SORT_DIRECTION = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
};

const PRESENCE_STATE = {
  NONE_PRESENT: 1,
  SOME_PRESENT: 2,
  ALL_PRESENT: 3,
};

/**
 * Determines the presence state of a group based on present students count
 * @param {Object} group - The group to evaluate
 * @returns {Array} - Array containing [stateCode, presentCount] where stateCode is:
 *   3 = all students present
 *   2 = some students present
 *   1 = no students present
 */
const getGroupPresenceState = (group) => {
  const totalStudents = group.maxMembers;
  const presentStudents = group.students.filter(
    student => student.isPresent,
  ).length;

  if (presentStudents === totalStudents) {
    return [PRESENCE_STATE.ALL_PRESENT, presentStudents];
  }
  if (presentStudents > 0) {
    return [PRESENCE_STATE.SOME_PRESENT, presentStudents];
  }
  return [PRESENCE_STATE.NONE_PRESENT, presentStudents];
};


/**
 * Compares two groups based on presence state
 * @param {Object} a - First group
 * @param {Object} b - Second group
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
const compareByPresence = (a, b, direction) => {
  const [presenceStateA, presentCountA] = getGroupPresenceState(a);
  const [presenceStateB, presentCountB] = getGroupPresenceState(b);

  const directionFactor = direction === SORT_DIRECTION.ASCENDING ? 1 : -1;

  if (presenceStateA !== presenceStateB) {
    return (presenceStateB - presenceStateA) * directionFactor;
  }

  // If both states are SOME_PRESENT, compare the number of present students
  if (presenceStateA === PRESENCE_STATE.SOME_PRESENT) {
    return (presentCountB - presentCountA) * directionFactor;
  }

  // If states are the same (ALL_PRESENT or NONE_PRESENT), groups are equal in this regard
  return 0;
};

/**
 * Compares two groups based on the first student's name
 * @param {Object} a - First group
 * @param {Object} b - Second group
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
const compareByClientName = (a, b, direction, lessonTypes) => {
  const getClientName = (group) => {
    if (isGroupReportLessonType(group.classType, lessonTypes)) {
      return "";
    }
    return (group.students[0] && group.students[0].clientName) || "";
  };

  const clientA = getClientName(a).toLowerCase();
  const clientB = getClientName(b).toLowerCase();

  if (!clientA && clientB) return 1;
  if (clientA && !clientB) return -1;

  if (clientA < clientB) {
    return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
  }
  if (clientA > clientB) {
    return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
  }
  return 0;
};

/**
 * Compares two groups based on the first student's name
 * @param {Object} a - First group
 * @param {Object} b - Second group
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
const compareByStudentName = (a, b, direction) => {
  const firstStudentA = a.students[0] ? a.students[0].studentName : "";
  const firstStudentB = b.students[0] ? b.students[0].studentName : "";

  if (firstStudentA < firstStudentB) {
    return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
  }
  if (firstStudentA > firstStudentB) {
    return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
  }
  return 0;
};

/**
 * Compares two groups based on the first student's comment
 * @param {Object} a - First group
 * @param {Object} b - Second group
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
const compareByComment = (a, b, direction) => {
  // Sort students within each group by comment
  const sortedStudentsA = [...a.students].sort((studentA, studentB) => {
    const commentA = studentA.comment.toLowerCase();
    const commentB = studentB.comment.toLowerCase();

    if (commentA < commentB) return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
    if (commentA > commentB) return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
    return 0;
  });

  const sortedStudentsB = [...b.students].sort((studentA, studentB) => {
    const commentA = studentA.comment.toLowerCase();
    const commentB = studentB.comment.toLowerCase();

    if (commentA < commentB) return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
    if (commentA > commentB) return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
    return 0;
  });

  const firstCommentA = sortedStudentsA[0]
    ? sortedStudentsA[0].comment.toLowerCase()
    : "";
  const firstCommentB = sortedStudentsB[0]
    ? sortedStudentsB[0].comment.toLowerCase()
    : "";

  if (firstCommentA < firstCommentB) {
    return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
  }
  if (firstCommentA > firstCommentB) {
    return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
  }
  return 0;
};

/**
 * Compares two groups based on day of week
 * @param {Object} a - First group
 * @param {Object} b - Second group
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
const compareByDayOfWeek = (a, b, direction) => {
  const dayA = DAY_ORDER[a.dayOfWeek] || 8;
  const dayB = DAY_ORDER[b.dayOfWeek] || 8;
  return direction === SORT_DIRECTION.ASCENDING
    ? dayA - dayB
    : dayB - dayA;
};

/**
 * Default comparison for any other field
 * @param {Object} a - First group
 * @param {Object} b - Second group
 * @param {string} key - The key to compare
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
const compareByDefault = (a, b, key, direction) => {
  if (a[key] < b[key]) {
    return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
  }
  if (a[key] > b[key]) {
    return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
  }
  return 0;
};

/**
 * Sorts students within a group by presence and name
 * @param {Array} students - Array of student objects
 * @param {string} direction - Sort direction ('ascending' or 'descending')
 * @returns {Array} - Sorted array of students
 */
const sortStudentsInGroup = (students, direction) => [...students].sort((studentA, studentB) => {
  // First sort by presence
  if (studentA.isPresent !== studentB.isPresent) {
    if (studentA.isPresent) {
      return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
    }
    return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
  }

  // Then sort by name
  if (studentA.studentName < studentB.studentName) {
    return direction === SORT_DIRECTION.ASCENDING ? -1 : 1;
  }
  if (studentA.studentName > studentB.studentName) {
    return direction === SORT_DIRECTION.ASCENDING ? 1 : -1;
  }

  return 0;
});

/**
 * Main function to sort attendance data
 * @param {Array} attendance - Array of attendance group objects
 * @param {Object} sortConfig - Sort configuration {key, direction}
 * @returns {Array} - Sorted attendance data
 */
export function sortAttendanceData(attendance, sortConfig, lessonTypes) {
  if (!sortConfig || !sortConfig.key) {
    return attendance;
  }

  const sortableItems = [...attendance];
  const { key, direction } = sortConfig;

  // Sort groups based on the sort key
  sortableItems.sort((a, b) => {
    if (key === "isPresent") {
      return compareByPresence(a, b, direction);
    }

    if (key === "studentName") {
      return compareByStudentName(a, b, direction);
    }

    if (key === "comment") {
      return compareByComment(a, b, direction);
    }

    if (key === "dayOfWeek") {
      return compareByDayOfWeek(a, b, direction);
    }

    if (key === "clientName") {
      return compareByClientName(a, b, direction, lessonTypes);
    }

    return compareByDefault(a, b, key, direction);
  });

  // Sort students within each group
  return sortableItems.map(group => ({
    ...group,
    students: sortStudentsInGroup(group.students, direction),
  }));
}
